import { Button } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import Table from "antd/es/table";
import React from "react";
import { useParams } from "react-router-dom";
import { Title } from "../../components/Desc";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import {
  useGetAllDesignationsQuery,
  useGetTimeslotPersonnelDetailsQuery,
  useGetTimeslotsQuery,
} from "../../services/appln";
import {
  getLocationKinds,
  getLocationStrMap,
  getReverseLocationStrMap,
} from "../util";
import * as XLSX from "xlsx";

export default function SummaryReport() {
  const params = useParams();

  const [timeslotMap, setTimeslotMap] = React.useState<any>(
    new Map<string, string>(),
  );
  const [selectedTimeslot, setSelectedTimeslot] = React.useState<any>("");

  const [selectedLocationKinds, setSelectedLocationKinds] = React.useState<any>(
    [strings.area, strings.point, strings.range, strings.subarea],
  );
  const [designationNames, setDesignationNames] = React.useState<any>([]);

  const [showAssessmentCount, setShowAssessmentCount] = React.useState(true);
  const [showAllocationCount, setShowAllocationCount] = React.useState(true);
  const [showAllocationPending, setShowAllocationPending] =
    React.useState(true);

  // selected designations
  const [selectedDesignations, setSelectedDesignations] = React.useState<
    Set<string>
  >(new Set<string>());

  const [rawData, setRawData] = React.useState<any>([]);

  interface LocationInfo {
    id: string;
    name: string;
    kind: string;
    details: any;
  }

  const detailsQueryResponse = useGetTimeslotPersonnelDetailsQuery({
    bandobastId: params.bandobastId,
    timeslotId: timeslotMap.get(selectedTimeslot)!,
  });

  React.useEffect(() => {
    if (!detailsQueryResponse.isSuccess) {
      return;
    }
    setRawData(detailsQueryResponse.data.data);
  }, [detailsQueryResponse]);

  const timeslotsQueryResponse = useGetTimeslotsQuery(params.bandobastId);
  React.useEffect(() => {
    if (!timeslotsQueryResponse.isSuccess) {
      return;
    }
    const timeslotMap = new Map<string, string>();
    timeslotsQueryResponse.data?.data.forEach((element: any) => {
      timeslotMap.set(element.name, element.id);
    });
    setTimeslotMap(timeslotMap);
  }, [timeslotsQueryResponse]);

  const designationsQueryResponse = useGetAllDesignationsQuery({
    page: 1,
    limit: 1000,
    search: "",
  });
  React.useEffect(() => {
    if (!designationsQueryResponse.isSuccess) {
      return;
    }
    const designationNames = designationsQueryResponse.data.data
      .map((e: any) => {
        return e.abbreviation ? e.abbreviation : e.name;
      })
      .sort();
    // console.log("designationNames", designationNames);
    setDesignationNames(designationNames);
    setSelectedDesignations(new Set<string>(designationNames));
  }, [designationsQueryResponse]);

  if (
    detailsQueryResponse.isLoading ||
    detailsQueryResponse.isFetching ||
    timeslotsQueryResponse.isLoading ||
    timeslotsQueryResponse.isFetching ||
    designationsQueryResponse.isLoading ||
    designationsQueryResponse.isFetching
  ) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          width: "100%",
          marginTop: "10px",
          marginLeft: "20px",
          marginRight: "20px",
        }}
      >
        <Title text={strings.personnelAssessmentAllocationSummary}></Title>
        <ProgressIndicator />
      </Box>
    );
  }

  // console.log("SummaryReport, selectedTimeslot", selectedTimeslot);
  // console.log("SummaryReport, selectedLocationKinds", selectedLocationKinds);
  console.log("SummaryReport, rawData", rawData);

  const locationMap = getReverseLocationStrMap();
  const locationKindsUpdated = selectedLocationKinds.map((e: string) => {
    return locationMap.get(e);
  });
  // console.log("SummaryReport, locationKindsUpdated", locationKindsUpdated);

  const rows = rawData.filter((e: any) => {
    return locationKindsUpdated.indexOf(e.kind) > -1;
  });
  // console.log("SummaryReport, rows", rows);

  const columns: ColumnsType<LocationInfo> = [
    // uncomment following to debug sequence related issues.
    // {
    //   title: strings.serialNo,
    //   dataIndex: "serialNo",
    //   key: "serialNo",
    // },
    {
      title: strings.name,
      dataIndex: "name",
      key: "name",
    },
    {
      title: strings.kind,
      dataIndex: "kind",
      key: "kind",
      render: (text, record, index) => getLocationStrMap().get(text)!,
    },
  ];

  designationNames.forEach((name: string) => {
    // add designation column only if its selected
    if (selectedDesignations.has(name)) {
      columns.push({
        title: name,
        dataIndex: name,
        key: name,
        render: (text, record, index) => {
          // console.log("text, record, index", text, record, index);
          const row: any = record["details"].find((e: any) => {
            if (e.abbreviation !== "") {
              return e.abbreviation === name;
            }
            return e.designationName === name;
          });
          // console.log("row", row);
          // console.log("name", name);
          return (
            <Space direction="horizontal">
              {showAssessmentCount && (
                <Tag color="magenta">{row["assessmentCount"]}</Tag>
              )}
              {showAllocationCount && (
                <Tag color="cyan">{row["allocationCount"]}</Tag>
              )}
              {showAllocationPending && (
                <Tag color="blue">{row["allocationPendingCount"]}</Tag>
              )}
            </Space>
          );
        },
      });
    }
  });

  return (
    <div>
      <Box
        sx={{
          height: 400,
          width: "100%",
          marginTop: "10px",
          marginLeft: "20px",
          marginRight: "20px",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Title text={strings.personnelAssessmentAllocationSummary}></Title>
          </Grid>
          <Grid item xs={7}>
            <Autocomplete
              options={Array.from(timeslotMap.keys())}
              value={selectedTimeslot}
              onChange={(e, value) => {
                setSelectedTimeslot(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="standard"
                  label={strings.timeslot}
                />
              )}
            />
          </Grid>
          <Grid item xs={7}>
            <Autocomplete
              multiple
              options={getLocationKinds()}
              value={selectedLocationKinds}
              onChange={(e, value) => {
                console.log("selected location kinds", value);
                setSelectedLocationKinds(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="standard"
                  label={strings.kind}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <FormGroup row={true}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAssessmentCount}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowAssessmentCount(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={strings.showAssessmentCount}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAllocationCount}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowAllocationCount(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={strings.showAllocationCount}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAllocationPending}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowAllocationPending(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={strings.showAllocationPending}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <FormGroup row={true}>
              {designationNames.map((name: string) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedDesignations.has(name)}
                      color="secondary"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        // console.log(
                        //   "name:",
                        //   name,
                        //   "event.target.checked:",
                        //   event.target.checked,
                        // );
                        if (event.target.checked) {
                          selectedDesignations.add(name);
                        } else {
                          selectedDesignations.delete(name);
                        }
                        // create a copy since updating existing copy does not refresh the ui
                        const newSelectedDesignations = new Set<string>([
                          ...selectedDesignations,
                        ]);
                        // console.log("newSelectedDesignations:", [
                        //   ...newSelectedDesignations,
                        // ]);
                        setSelectedDesignations(newSelectedDesignations);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label={name}
                />
              ))}
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={() => {
                // console.log("in export handler");
                console.log("excel sheet rows", rows);
                const assessmentTotalsCount = new Map<string, number>();
                const allocationTotalsCount = new Map<string, number>();
                const designationSet = new Set<string>();
                designationNames.forEach((designationName: string) => {
                  assessmentTotalsCount.set(designationName, 0);
                  allocationTotalsCount.set(designationName, 0);
                  designationSet.add(designationName);
                });
                const excelData = rows.map((row: any) => {
                  const output: any = {};
                  output[strings.name] = row.name;
                  output[strings.kind] = getLocationStrMap().get(row.kind)!;
                  const details = row.details;
                  details.forEach((entry: any) => {
                    const designationName = entry["designationName"];
                    const abbreviation = entry["abbreviation"];

                    const key = designationSet.has(abbreviation)
                      ? abbreviation
                      : designationName;
                    console.log("key", key);

                    if (!assessmentTotalsCount.has(key)) {
                      assessmentTotalsCount.set(key, 0);
                    }
                    if (!allocationTotalsCount.has(key)) {
                      allocationTotalsCount.set(key, 0);
                    }
                    if (selectedDesignations.has(key)) {
                      if (showAssessmentCount) {
                        output[`${key} - Assessment count`] =
                          entry["assessmentCount"];
                      }
                      if (showAllocationCount) {
                        output[`${key} - Allocated count`] =
                          entry["allocationCount"];
                      }
                      if (showAllocationPending) {
                        output[`${key} - Allocated pending count`] =
                          entry["allocationPendingCount"];
                      }

                      assessmentTotalsCount.set(
                        key,
                        assessmentTotalsCount.get(key)! +
                          entry["assessmentCount"],
                      );
                      allocationTotalsCount.set(
                        key,
                        allocationTotalsCount.get(key)! +
                          entry["allocationCount"],
                      );
                    }
                  });
                  return output;
                });
                const totalRow: any = {};
                totalRow[strings.name] = strings.total;
                totalRow[strings.kind] = "";
                designationNames.forEach((name: string) => {
                  if (selectedDesignations.has(name)) {
                    if (showAssessmentCount) {
                      totalRow[`${name} - Assessment count`] =
                        assessmentTotalsCount.get(name)!;
                    }
                    if (showAllocationCount) {
                      totalRow[`${name} - Allocated count`] =
                        allocationTotalsCount.get(name)!;
                    }
                    if (showAllocationPending) {
                      totalRow[`${name} - Allocated pending count`] =
                        assessmentTotalsCount.get(name)! -
                        allocationTotalsCount.get(name)!;
                    }
                  }
                });
                excelData.push(totalRow);
                const worksheet = XLSX.utils.json_to_sheet(excelData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(
                  workbook,
                  worksheet,
                  selectedTimeslot,
                );
                XLSX.writeFile(workbook, "report.xlsx");
              }}
            >
              {strings.export}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Table
              scroll={{ y: 300 }}
              columns={columns}
              dataSource={rows}
              pagination={false}
              summary={(pageData) => {
                console.log("pageData", pageData);
                const assessmentTotalsCount = new Map<string, number>();
                const allocationTotalsCount = new Map<string, number>();
                const designationSet = new Set<string>();
                designationNames.forEach((designationName: string) => {
                  assessmentTotalsCount.set(designationName, 0);
                  allocationTotalsCount.set(designationName, 0);
                  designationSet.add(designationName);
                });
                pageData.forEach((e: any) => {
                  const details = e["details"];
                  details.forEach((entry: any) => {
                    // console.log("entry", entry);

                    const desigName = entry["designationName"];
                    // console.log("desigName", desigName);

                    const abbreviation = entry["abbreviation"];
                    // console.log("abbreviation", abbreviation);

                    const key = designationSet.has(abbreviation)
                      ? abbreviation
                      : desigName;
                    // console.log("key", key);

                    if (!assessmentTotalsCount.has(key)) {
                      assessmentTotalsCount.set(key, 0);
                    }
                    if (!allocationTotalsCount.has(key)) {
                      allocationTotalsCount.set(key, 0);
                    }

                    assessmentTotalsCount.set(
                      key,
                      assessmentTotalsCount.get(key)! +
                        entry["assessmentCount"],
                    );
                    allocationTotalsCount.set(
                      key,
                      allocationTotalsCount.get(key)! +
                        entry["allocationCount"],
                    );

                    // console.log("assessmentTotalsCount", assessmentTotalsCount);
                    // console.log("allocationTotalsCount", allocationTotalsCount);
                  });
                });

                console.log("assessmentTotalsCount", assessmentTotalsCount);
                console.log("allocationTotalsCount", allocationTotalsCount);

                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <b>{strings.total}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={0}></Table.Summary.Cell>
                      {designationNames.map(
                        (designatioName: string, index: number) => {
                          if (!selectedDesignations.has(designatioName)) {
                            return <></>;
                          }
                          return (
                            <>
                              <Table.Summary.Cell index={index + 1}>
                                <Space direction="horizontal">
                                  {showAssessmentCount && (
                                    <Tag color="magenta">
                                      {
                                        assessmentTotalsCount.get(
                                          designatioName,
                                        )!
                                      }
                                    </Tag>
                                  )}
                                  {showAllocationCount && (
                                    <Tag color="cyan">
                                      {
                                        allocationTotalsCount.get(
                                          designatioName,
                                        )!
                                      }
                                    </Tag>
                                  )}
                                  {showAllocationPending && (
                                    <Tag color="blue">
                                      {assessmentTotalsCount.get(
                                        designatioName,
                                      )! -
                                        allocationTotalsCount.get(
                                          designatioName,
                                        )!}
                                    </Tag>
                                  )}
                                </Space>
                              </Table.Summary.Cell>
                            </>
                          );
                        },
                      )}
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
